import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { QRCodeSVG } from "qrcode.react";
import { buildPhoneRecordingUrl } from "services/chartMindPhoneRelayService";
import { SUPPORTED_LANGUAGES } from "components/dashboard/chartmind/common/LanguageSelector";

/** "English (US)" rather than "en-US" — the caller only has the locale code. */
const languageLabel = (code) =>
  SUPPORTED_LANGUAGES.find((lang) => lang.code === code)?.label || code;

/**
 * The last-resort ceiling on waiting for the sign-in grant.
 *
 * ⚠️ THIS IS NOT THE BUDGET THE CODE IS DRAWN AGAINST. The code waits for the mint to SETTLE —
 * land or fail — and this only catches a callable that does neither, which is the one case
 * where waiting forever would be worse than a code that asks for a password.
 *
 * It replaced a 1500ms wall-clock hold, and that number is the whole story of this bug. A warm
 * mint answers in about 200ms, so the hold looked generous; a COLD one takes four to six
 * seconds (measured, in sandbox logs). Every first pairing after an idle period therefore drew
 * the code before its credential existed, the phone opened a URL with no `g`/`s` in it and was
 * asked to sign in — and the second scan "fixed" it only because by then the mint had landed
 * and the QR had quietly become a different URL. Anything that draws the code against a clock
 * instead of against the mint brings that back.
 */
const GRANT_WAIT_CEILING_MS = 12000;


/**
 * Pairing dialog for "Record on your phone".
 *
 * The QR is a plain https deep link so the phone's native camera opens it, which is why there
 * is no scanner to build. It carries the pairing id, plus a device-link grant when one could be
 * minted, so a phone with no session signs itself in from the same scan.
 *
 * ⚠️ THE CODE IS NEVER DRAWN BEFORE IT IS COMPLETE, and never changes from incomplete to
 * complete while it is up. Those two rules are the feature working or not working — see
 * GRANT_WAIT_CEILING_MS and gaveUpOnGrantRef below for what each one is defending against.
 *
 * ⚠️ THIS DIALOG ONLY EVER SHOWS A CODE. The moment the phone says hello, ChartMindPage closes
 * it and the Record step shows a dismissible success alert instead. Two reasons: a modal that
 * has stopped asking for anything is just something else to close, and the QR carries a
 * single-use sign-in grant, so there is no reason to keep an already-spent credential on a
 * screen in an exam room. Do not add a success state in here.
 */
const PhoneRecordingDialog = ({
  open,
  onClose,
  pairingId,
  grant = null,
  grantUnavailable = false,
  starting = false,
  error = "",
  language = "en-US",
  onRetry,
}) => {
  // The ceiling above, armed only while something is actually being waited for. Reopening on a
  // pairing that already has a live grant — every encounter after the first — waits for nothing
  // and arms nothing.
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  useEffect(() => {
    if (!open) {
      setWaitedTooLong(false);
      return undefined;
    }
    const timer = setTimeout(() => setWaitedTooLong(true), GRANT_WAIT_CEILING_MS);
    return () => clearTimeout(timer);
  }, [open]);

  /**
   * ⚠️ ONCE A CODE IS DRAWN WITHOUT A CREDENTIAL, IT STAYS THAT WAY FOR THIS OPENING.
   *
   * The failure this dialog is built around is not a slow QR, it is a QR that CHANGES IDENTITY
   * under a camera that is already pointed at it. A phone decodes the grant-less URL, its
   * scan banner keeps that URL, the code on screen silently becomes a different one, and the
   * person taps a link that signs nobody in — while looking straight at a code that would
   * have. So the swap is not allowed to happen: having given up, we keep showing what we gave
   * up with, and the caption says what that costs.
   *
   * A grant-to-grant swap is fine and still happens on the refresh (see the hook): the
   * superseded grant lives out its own two minutes, so a banner holding the older URL still
   * redeems. It is only none-to-grant that is a trap.
   */
  const gaveUpOnGrantRef = useRef(false);
  const gaveUp = grantUnavailable || waitedTooLong;
  if (!open) gaveUpOnGrantRef.current = false;
  else if (gaveUp && !grant) gaveUpOnGrantRef.current = true;

  // Wait on the GRANT, not on whether a mint happens to be in flight this instant: between a
  // failed attempt and its retry nothing is in flight, and that gap is still a gap to wait
  // through, not a reason to publish a code that signs nobody in.
  // A grant that lands AFTER we gave up is not drawn either, for the same reason: the code is
  // already on screen and being scanned. It is picked up the next time the dialog is opened.
  const displayedGrant = gaveUpOnGrantRef.current ? null : grant;
  const holdingForGrant = !displayedGrant && !gaveUpOnGrantRef.current;

  return (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="phone-recording-title">
    {/* Same shape as the app's other dialogs (SettingsDialog): title with a close button, no
        Cancel row, since there is nothing here to confirm. */}
    <DialogTitle
      id="phone-recording-title"
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
    >
      Record on your phone
      <IconButton aria-label="Close" edge="end" onClick={onClose}>
        <CloseRounded />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      {error ? (
        <Stack spacing={2} alignItems="flex-start">
          <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>
          <Button onClick={onRetry}>Try again</Button>
        </Stack>
      ) : starting || !pairingId || holdingForGrant ? (
        // This wait can be several seconds on a cold callable with a patient in the room, so
        // the spinner says what it is preparing rather than reading as a hang.
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Preparing the code for your phone
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={3} alignItems="center" sx={{ pt: 1, pb: 2, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Scan this with your phone&apos;s camera, then tap the microphone on your phone.
          </Typography>

          <Box
            sx={{
              display: "flex",
              p: 2,
              // Stays white in dark mode: scanners need dark-on-light with a quiet zone.
              bgcolor: "common.white",
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
            }}
          >
            <QRCodeSVG value={buildPhoneRecordingUrl(pairingId, displayedGrant)} size={200} />
          </Box>

          {/* Said up front rather than discovered on the phone: this code cannot sign anyone
              in, so the scan lands on a password prompt. */}
          {!displayedGrant && (
            <Alert severity="info" sx={{ textAlign: "left", width: "100%" }}>
              Your phone will ask you to sign in once.
            </Alert>
          )}

          {/* The language is named because it is fixed when the pairing opens: the phone
              cannot change it. */}
          <Typography variant="caption" color="text.secondary">
            Records in {languageLabel(language)}. Keep this tab open.
          </Typography>
        </Stack>
      )}
    </DialogContent>
  </Dialog>
  );
};

export default PhoneRecordingDialog;
